import React, { useState, useCallback, useEffect } from 'react';

const TIPS_LIST = [
  {
    title: "Three Principles to build a Smart Team:",
    body: "(1) Delegate, (2) Empower, (3) Monitor."
  },
  {
    title: "Old Customers are your Gold Mine.",
    body: "With ZERO marketing cost, they can give you Repeat Orders and Refer New Customers."
  },
  {
    title: "Sales Funnel can inspire you to close deals.",
    body: "Be sure to update Prospect Stage after every Interaction."
  },
  {
    title: "Sale entry is Simple but Super-Satisfying.",
    body: "Log your Victories and beat your Previous Month!"
  },
  {
    title: "Consolidate your Decision-making.",
    body: "List your Pending Decisions, review & decide when your brain is super-fresh."
  },
  {
    title: "Simplify Dependence of your Team.",
    body: "Ask them to Request Approval in Sahi, so you can consolidate decisions without being interrupted."
  },
  {
    title: "Sahi Club is for the Complete YOU.",
    body: "You can list & tick your own personal tasks - they are kept private even from company admins."
  },
  {
    title: "Sahi Meetings are Easy & Effective.",
    body: "Set the agenda, invite participants, welcome pre-meeting inputs, and record your decisions."
  },
  {
    title: "Every Inquiry must be Qualified Quickly!",
    body: "Check Stage & Last Interaction of your Prospects."
  },
  {
    title: "Hope you are enjoying our creation!",
    body: "Sahi Club is crafted to delight smart executives like you. Click Help to connect with us and share your ideas!"
  }
];

const SahiLoader = ({ message = "", progress = "" }) => {
  // Start with a random tip index
  const [tipIndex, setTipIndex] = useState(() => Math.floor(Math.random() * TIPS_LIST.length));
  const [fade, setFade] = useState(true);
  const [showContent, setShowContent] = useState(false);

  // Wait 2 seconds (approx. 2 dot bounce cycles) before showing text
  useEffect(() => {
    const delayTimer = setTimeout(() => {
      setShowContent(true);
    }, 2000);

    return () => clearTimeout(delayTimer);
  }, []);

  // Click/tap handler to advance to next tip (like in games)
  const handleNextTip = useCallback((e) => {
    if (!showContent) return;
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setFade(false);
    setTimeout(() => {
      setTipIndex((prev) => (prev + 1) % TIPS_LIST.length);
      setFade(true);
    }, 150);
  }, [showContent]);

  // Auto-change tip every 5 seconds (starts only after text is visible)
  useEffect(() => {
    if (!showContent) return;

    const timer = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setTipIndex((prev) => (prev + 1) % TIPS_LIST.length);
        setFade(true);
      }, 150);
    }, 5000);

    return () => clearInterval(timer);
  }, [tipIndex, showContent]);

  const currentTip = TIPS_LIST[tipIndex] || TIPS_LIST[0];

  return (
    <div
      className="fixed inset-0 z-[9999] bg-white flex flex-col items-center justify-center p-6 select-none overflow-hidden cursor-pointer"
      onClick={handleNextTip}
      onMouseDown={(e) => e.preventDefault()}
      onKeyDown={(e) => e.preventDefault()}
    >
      {/* Pinned Center: Three Dots Animated Loader & Operational Message */}
      <div className="flex flex-col items-center pointer-events-none text-center relative">
        {/* Mobile: Title placed above the dots */}
        <div
          className={`md:hidden absolute bottom-full mb-8 left-1/2 -translate-x-1/2 w-[85vw] max-w-[340px] text-center transition-all duration-700 ease-out cursor-pointer pointer-events-auto ${
            showContent
              ? fade
                ? 'opacity-100 translate-y-0 scale-100'
                : 'opacity-0 scale-95'
              : 'opacity-0 -translate-y-3 scale-95 pointer-events-none'
          }`}
        >
          <h3 className="text-[18px] font-semibold text-zinc-900 leading-snug">
            {currentTip.title}
          </h3>
        </div>

        {/* Center 3 Bouncing Dots */}
        <div className="flex justify-center items-center space-x-2.5">
          <div
            className="w-3.5 h-3.5 bg-sahi-orange rounded-full animate-bounce shadow-md shadow-sahi-orange/30"
            style={{ animationDelay: '0s' }}
          ></div>
          <div
            className="w-3.5 h-3.5 bg-sahi-dark-blue rounded-full animate-bounce shadow-md shadow-sahi-dark-blue/30"
            style={{ animationDelay: '0.15s' }}
          ></div>
          <div
            className="w-3.5 h-3.5 bg-sahi-green rounded-full animate-bounce shadow-md shadow-sahi-green/30"
            style={{ animationDelay: '0.3s' }}
          ></div>
        </div>

        {/* Dynamic Operational Message / Progress (if supplied) */}
        {message && (
          <div
            className={`mt-4 text-center animate-pulse transition-all duration-700 ease-out ${
              showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'
            }`}
          >
            <p className="text-base font-semibold text-zinc-800 tracking-wide">{message}</p>
            {progress && (
              <p className="text-xs text-zinc-500 mt-1 font-mono">{progress}</p>
            )}
          </div>
        )}

        {/* Mobile: Description placed below the dots */}
        <div
          className={`md:hidden absolute top-full mt-8 left-1/2 -translate-x-1/2 w-[85vw] max-w-[340px] text-center transition-all duration-700 ease-out cursor-pointer pointer-events-auto ${
            showContent
              ? fade
                ? 'opacity-100 translate-y-0 scale-100'
                : 'opacity-0 scale-95'
              : 'opacity-0 translate-y-3 scale-95 pointer-events-none'
          }`}
        >
          <p className="text-[14px] text-zinc-600 leading-relaxed font-normal">
            {currentTip.body}
          </p>
        </div>

        {/* Desktop: Title and Description together below the dots */}
        <div
          className={`hidden md:flex absolute top-full mt-8 left-1/2 -translate-x-1/2 md:max-w-lg md:w-[460px] flex-col gap-2 text-center transition-all duration-700 ease-out cursor-pointer pointer-events-auto ${
            showContent
              ? fade
                ? 'opacity-100 translate-y-0 scale-100'
                : 'opacity-0 scale-95'
              : 'opacity-0 translate-y-3 scale-95 pointer-events-none'
          }`}
        >
          <h3 className="text-[20px] font-semibold text-zinc-900 leading-snug">
            {currentTip.title}
          </h3>
          <p className="text-[16px] text-zinc-600 leading-relaxed font-normal">
            {currentTip.body}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SahiLoader;